import { useEffect, useRef, useState } from 'react'
import {
  ArrowRight,
  GitPullRequest,
  ListChecks,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  Wand2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { motion, useInView, useReducedMotion } from 'motion/react'
import { cn } from '../../lib/cn'

const EASE = [0.22, 1, 0.36, 1] as const

/* ----------------------------------------------------------------
 * One looping "Langy session". A master phase counter walks the
 * five stages — goal, plan, run, score, ship — holds the end state,
 * then replays. The left-hand step list lights up in sync.
 * ---------------------------------------------------------------- */

const GOAL = 'Test the refund agent against frustrated EU customers in DE / FR / NL.'

// phase index → how long it stays current (ms)
const PHASE_MS = [2400, 2200, 3000, 2000, 2600]
const HOLD_MS = 4000
const LAST = PHASE_MS.length - 1

type Stage = { icon: LucideIcon; title: string; body: string; meta: string }

const stages: Stage[] = [
  {
    icon: MessageSquare,
    title: 'PM writes the goal',
    body: 'Plain English. No code, no YAML. The brief is the spec.',
    meta: 'no code',
  },
  {
    icon: Wand2,
    title: 'Langy drafts the plan',
    body: 'Picks the simulator, generates the scenarios, writes the JudgeAgent rubric.',
    meta: 'live',
  },
  {
    icon: ListChecks,
    title: 'Scenario runs in parallel',
    body: 'Multi-turn conversations against your agent, concurrent across projects.',
    meta: 'parallel',
  },
  {
    icon: ShieldCheck,
    title: 'JudgeAgent scores it',
    body: 'Your rubric, audited. Faithfulness, policy adherence, de-escalation.',
    meta: 'signed',
  },
  {
    icon: GitPullRequest,
    title: 'Regressions become PRs',
    body: 'Langy drafts the prompt revision. Devs review and ship via Prompt Registry.',
    meta: 'ready to ship',
  },
]

const PLAN_CHIPS = [
  'simulator · UserSimulatorAgent',
  '32 scenarios · DE / FR / NL',
  'rubric · 4 criteria',
]

const RUNS = [
  { name: 'refund · within policy', turns: 6, ok: true },
  { name: 'refund · outside policy', turns: 8, ok: true },
  { name: 'angry escalation (DE)', turns: 9, ok: false },
  { name: 'partial refund · negotiation', turns: 7, ok: true },
]

function useSessionPhase(active: boolean, instant: boolean) {
  // Always start at -1: `instant` comes from useReducedMotion(), which is
  // false during SSR but true on the client's first render when the user (or
  // Lighthouse) prefers reduced motion. Seeding state from it made the served
  // HTML and the hydration render disagree (React #418), so React re-rendered
  // the whole page from scratch. The instant jump to the final phase happens
  // in the effect below instead, after hydration.
  const [phase, setPhase] = useState(-1)

  useEffect(() => {
    if (instant) {
      setPhase(LAST)
      return
    }
    if (!active) return
    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []
    const schedule = (fn: () => void, ms: number) => {
      if (!cancelled) timers.push(setTimeout(fn, ms))
    }
    const run = () => {
      setPhase(0)
      let at = 0
      for (let p = 1; p <= LAST; p++) {
        at += PHASE_MS[p - 1]
        const next = p
        schedule(() => setPhase(next), at)
      }
      schedule(run, at + PHASE_MS[LAST] + HOLD_MS)
    }
    schedule(run, 500)
    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
    }
  }, [active, instant])

  return phase
}

/* Typewriter for the PM goal during phase 0. */
function TypedGoal({ play, instant }: { play: boolean; instant: boolean }) {
  const [n, setN] = useState(instant ? GOAL.length : 0)

  useEffect(() => {
    if (instant) return
    if (!play) {
      setN(0)
      return
    }
    let i = 0
    const interval = setInterval(() => {
      i += 2
      setN(Math.min(i, GOAL.length))
      if (i >= GOAL.length) clearInterval(interval)
    }, 32)
    return () => clearInterval(interval)
  }, [play, instant])

  return (
    <span className="text-paper/90">
      {GOAL.slice(0, n)}
      {!instant && n < GOAL.length && (
        <span className="ml-px inline-block h-[13px] w-[7px] translate-y-[2px] animate-pulse bg-brand-300/80" />
      )}
    </span>
  )
}

function SessionRow({
  visible,
  ghostLabel,
  minH,
  children,
}: {
  visible: boolean
  ghostLabel: string
  /** reserved height when the row is in ghost state — keeps the card from jumping */
  minH: number
  children: React.ReactNode
}) {
  return (
    <div className="relative" style={{ minHeight: minH }}>
      {!visible && (
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="space-y-2.5"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.03em] text-paper/25">
            <span className="h-1 w-1 rounded-full bg-paper/25" />
            <span>{ghostLabel}</span>
            <span className="text-paper/20">· pending</span>
          </div>
          <div className="h-2 w-2/3 rounded-full bg-white/[0.04]" />
          <div className="h-2 w-1/3 rounded-full bg-white/[0.04]" />
        </motion.div>
      )}
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
        >
          {children}
        </motion.div>
      )}
    </div>
  )
}

const PHASE_LABELS = ['goal', 'plan', 'run', 'score', 'ship'] as const

function PhaseProgress({ phase, shipped }: { phase: number; shipped: boolean }) {
  if (shipped) {
    return (
      <span className="text-[11px] text-brand-300">goal → PR · 14 min</span>
    )
  }
  return (
    <span className="flex items-center gap-1.5 text-[11px]">
      {PHASE_LABELS.map((label, i) => {
        const done = phase > i
        const current = phase === i
        return (
          <span key={label} className="flex items-center gap-1.5">
            <span
              className={`transition-colors duration-300 ${
                current
                  ? 'text-brand-300'
                  : done
                    ? 'text-paper/70'
                    : 'text-paper/25'
              }`}
            >
              {label}
            </span>
            {i < PHASE_LABELS.length - 1 && (
              <span
                className={`transition-colors duration-300 ${
                  done ? 'text-paper/50' : 'text-paper/20'
                }`}
              >
                →
              </span>
            )}
          </span>
        )
      })}
    </span>
  )
}

function LangySession({ phase, instant }: { phase: number; instant: boolean }) {
  const started = phase >= 0
  const scored = phase >= 3
  const shipped = phase >= 4

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.03em] text-paper/55">
          <Sparkles className="h-3 w-3 text-brand-300" />
          langy · live session
        </div>
        <PhaseProgress phase={phase} shipped={shipped} />
      </div>

      <div className="flex flex-col gap-4 px-5 py-5">
        {/* 01 — the goal, typed */}
        <SessionRow visible={started} ghostLabel="pm · goal" minH={56}>
          <div className="text-[10px] uppercase tracking-[0.03em] text-paper/40">
            pm · goal
          </div>
          <p className="mt-1.5 text-[13px] leading-relaxed">
            <TypedGoal play={started} instant={instant} />
          </p>
        </SessionRow>

        {/* 02 — the plan assembles */}
        <SessionRow visible={phase >= 1} ghostLabel="langy · plan" minH={62}>
          <div className="text-[10px] uppercase tracking-[0.03em] text-paper/40">
            langy · plan
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {PLAN_CHIPS.map((chip, i) => (
              <motion.span
                key={chip}
                initial={false}
                animate={
                  phase >= 1 ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.96 }
                }
                transition={{ duration: 0.35, ease: EASE, delay: phase >= 1 ? i * 0.18 : 0 }}
                className="rounded-full border border-brand-300/30 bg-brand-300/10 px-2.5 py-1 text-[11px] text-brand-300"
              >
                {chip}
              </motion.span>
            ))}
          </div>
        </SessionRow>

        {/* 03 — runs race in parallel */}
        <SessionRow visible={phase >= 2} ghostLabel="langy · run" minH={158}>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.03em] text-paper/40">
            <span>scenario · running 32 concurrent</span>
            <span
              className={`transition-opacity duration-300 ${scored ? 'opacity-0' : 'opacity-100'}`}
            >
              <span className="relative mr-1.5 inline-flex h-1.5 w-1.5">
                <span className="absolute inset-0 animate-ping rounded-full bg-brand-300/60" />
                <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-brand-300" />
              </span>
              live
            </span>
          </div>
          <div className="mt-2 space-y-1.5">
            {RUNS.map((r, i) => {
              const settled = scored || instant
              const caught = settled && !r.ok
              return (
                <motion.div
                  key={r.name}
                  initial={false}
                  animate={
                    caught
                      ? { x: [0, -3, 3, -2, 0] }
                      : { x: 0 }
                  }
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                  className={`flex items-center gap-3 rounded-md transition-colors duration-300 ${
                    caught
                      ? '-mx-1.5 px-1.5 py-0.5 bg-rust-accent/10 ring-1 ring-inset ring-rust-accent/30'
                      : ''
                  }`}
                >
                  <span className="w-[130px] shrink-0 truncate text-[11.5px] text-paper/70 sm:w-[210px]">
                    {r.name}
                  </span>
                  <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      initial={false}
                      animate={{ width: phase >= 2 ? '100%' : '0%' }}
                      transition={{
                        duration: instant ? 0 : 1.6 + i * 0.35,
                        ease: 'easeInOut',
                      }}
                      className={`absolute inset-y-0 left-0 rounded-full ${
                        settled
                          ? r.ok
                            ? 'bg-moss-accent'
                            : 'bg-rust-accent'
                          : 'bg-brand-300/70'
                      }`}
                    />
                  </div>
                  <span
                    className={`w-[88px] text-right text-[11px] transition-opacity duration-300 ${
                      settled ? 'opacity-100' : 'opacity-0'
                    } ${r.ok ? 'text-moss-accent' : 'text-rust-accent'}`}
                  >
                    {r.ok ? 'pass' : 'regression'}
                  </span>
                </motion.div>
              )
            })}
            <div className="pt-0.5 text-[10.5px] text-paper/35">
              + 28 more · {RUNS[0].turns}–{RUNS[2].turns} turns each
            </div>
          </div>
        </SessionRow>

        {/* 04 — judge verdict */}
        <SessionRow visible={scored} ghostLabel="langy · judge" minH={48}>
          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5">
            <span className="text-[11.5px] text-paper/70">
              JudgeAgent · <span className="text-moss-accent">28 pass</span> ·{' '}
              <span className="text-rust-accent">4 regressions</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.03em] text-paper/40">
              rubric audited · signed
            </span>
          </div>
        </SessionRow>

        {/* 05 — the PR ships */}
        <SessionRow visible={shipped} ghostLabel="langy · ship" minH={66}>
          <div className="flex items-center gap-3 rounded-lg border border-brand-300/30 bg-brand-300/[0.08] px-3.5 py-3">
            <GitPullRequest className="h-4 w-4 shrink-0 text-brand-300" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] text-paper">
                fix: tighten refund-policy prompt for DE escalations
              </div>
              <div className="mt-0.5 text-[10.5px] text-paper/45">
                drafted by langy · 4 failing scenarios attached ·{' '}
                <span className="text-moss-accent">+12</span>{' '}
                <span className="text-rust-accent">−4</span>
              </div>
            </div>
            <span className="shrink-0 rounded-full border border-brand-300/40 px-2 py-0.5 text-[10px] text-brand-300">
              review
            </span>
          </div>
        </SessionRow>
      </div>

      <div className="flex items-center justify-between border-t border-white/10 px-5 py-3 text-[11.5px] text-paper/55">
        <span>
          median PM-to-PR <span className="text-paper">14 minutes</span>
        </span>
        <a
          href="/langy"
          className="text-brand-300 underline-offset-4 transition hover:underline"
        >
          watch Langy work →
        </a>
      </div>
    </div>
  )
}

export function SectionLangy() {
  const prefersReducedMotion = useReducedMotion() ?? false
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-120px' })
  const phase = useSessionPhase(inView, prefersReducedMotion)

  const rise = (delay: number) =>
    prefersReducedMotion
      ? { initial: false as const, animate: { opacity: 1, y: 0 } }
      : {
          initial: { opacity: 0, y: 8 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: '-80px' },
          transition: { duration: 0.5, ease: EASE, delay },
        }

  return (
    <section id="langy" data-nav-dark className="relative scroll-mt-0 overflow-hidden text-paper">
      <div ref={ref} className="container-rail relative z-10 py-24 md:py-32">
        <div className="max-w-[720px]">
          <motion.h2
            {...rise(0.06)}
            className="display text-[30px] leading-[1.06] text-paper md:text-[44px]"
          >
            Our AI tests your AI
          </motion.h2>
          <motion.p
            {...rise(0.12)}
            className="mt-5 text-[17px] leading-[1.6] text-paper/65 md:text-[18px]"
          >
            Langy turns a PM's goal into a full Scenario test plan, then
            turns the failures into pull requests.
          </motion.p>
          <motion.p {...rise(0.16)} className="mt-4 text-[16px] leading-[1.6] text-paper/55">
            PMs own the spec. Devs stay in flow. Nothing slips through.
          </motion.p>

          <motion.div {...rise(0.2)} className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href="/langy"
              className="inline-flex items-center gap-2 rounded-full bg-paper px-5 py-3 text-[13.5px] font-medium text-ink-900 transition hover:bg-paper-soft"
            >
              See what Langy does
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/get-a-demo"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-[13.5px] font-medium text-paper transition hover:bg-white/10"
            >
              Book a call
            </a>
          </motion.div>
        </div>

        {/* stage row, lit in sync with the running session below - a scripted
            narrative rather than user-selectable tabs, read-only progress
            tiles rather than a click-to-switch tab bar */}
        <motion.div
          {...rise(0.26)}
          className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
        >
          {stages.map((s, i) => {
            const Icon = s.icon
            const lit = prefersReducedMotion || phase >= i
            const current = !prefersReducedMotion && phase === i
            return (
              <div
                key={s.title}
                className={cn(
                  'rounded-2xl border p-4 transition-colors duration-400',
                  current ? 'border-brand-300/40 bg-white/[0.06]' : 'border-white/10',
                )}
              >
                <span
                  className={cn(
                    'grid h-9 w-9 place-items-center rounded-lg border transition-colors duration-400',
                    lit
                      ? 'border-brand-300/40 bg-brand-300/10 text-brand-300'
                      : 'border-white/10 text-paper/30',
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <div
                  className={cn(
                    'mt-3 text-[13.5px] font-semibold tracking-tight transition-colors duration-400',
                    lit ? 'text-paper' : 'text-paper/40',
                  )}
                >
                  {s.title}
                </div>
                <div
                  className={cn(
                    'mt-1 text-[11.5px] leading-relaxed transition-colors duration-400',
                    lit ? 'text-paper/55' : 'text-paper/25',
                  )}
                >
                  {s.body}
                </div>
                <div
                  className={cn(
                    'mt-2 inline-block rounded-full border px-1.5 py-px text-[9.5px] font-normal transition-colors duration-400',
                    lit
                      ? 'border-white/15 text-paper/60'
                      : 'border-white/10 text-paper/30',
                  )}
                >
                  {s.meta}
                </div>
              </div>
            )
          })}
        </motion.div>

        <motion.div {...rise(0.32)} className="mt-8">
          <LangySession phase={phase} instant={prefersReducedMotion} />
        </motion.div>
      </div>
    </section>
  )
}
